import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { api } from '../api';
import { AuthContext } from '../App';
import Chat from '../components/Chat';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8083';
const TIP_PRESETS = [100, 200, 500, 1000, 2000, 5000];

export default function WatchPage() {
  const { username } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [streamData, setStreamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [raidBanner, setRaidBanner] = useState(null);
  const [theater, setTheater] = useState(false);

  const [tipAmount, setTipAmount] = useState(500);
  const [tipMsg, setTipMsg] = useState('');
  const [tipName, setTipName] = useState(user?.display_name || '');
  const [tipping, setTipping] = useState(false);
  const [tipStatus, setTipStatus] = useState('');

  const [subscribing, setSubscribing] = useState(false);
  const [subStatus, setSubStatus] = useState('');
  const [following, setFollowing] = useState(false);

  const [points, setPoints] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [redeemStatus, setRedeemStatus] = useState('');

  // Polls
  const [poll, setPoll] = useState(null);
  const [voted, setVoted] = useState(false);

  // Ratings
  const [ratings, setRatings] = useState({ helpful: 0, entertaining: 0, creative: 0 });
  const [userRating, setUserRating] = useState(null);

  // Goal
  const [goal, setGoal] = useState(null);

  // Gift sub
  const [showGift, setShowGift] = useState(false);
  const [giftRecipient, setGiftRecipient] = useState('');
  const [giftRandom, setGiftRandom] = useState(true);
  const [gifting, setGifting] = useState(false);
  const [giftStatus, setGiftStatus] = useState('');

  useEffect(() => {
    api.stream(username).then(d => {
      setStreamData(d);
      if (d.goal_text) setGoal({ text: d.goal_text, progress: d.goal_progress || 0, target: d.goal_target || 100 });
    }).catch(console.error).finally(() => setLoading(false));
    api.listRewards(username).then(setRewards).catch(() => {});
    api.activePoll(username).then(p => { if (p) setPoll(p); }).catch(() => {});
    api.streamRatings(username).then(d => { setRatings(d.ratings); setUserRating(d.user_rating); }).catch(() => {});
    if (user) api.getPoints(username).then(d => setPoints(d.points)).catch(() => {});
  }, [username]);

  useEffect(() => {
    if (!user || !streamData?.is_live) return;
    const interval = setInterval(() => {
      api.earnPoints(username).then(d => setPoints(d.points)).catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, [user, username, streamData?.is_live]);

  const handleRedeem = async (reward) => {
    setRedeemStatus('');
    try {
      const res = await api.redeemReward(reward.id);
      setPoints(res.points_remaining);
      setRedeemStatus(`"${reward.title}" redeemed!`);
    } catch (e) { setRedeemStatus(e.message); }
    setTimeout(() => setRedeemStatus(''), 4000);
  };

  // SocketIO for raids + polls
  useEffect(() => {
    const socket = io(BASE, { transports: ['websocket', 'polling'] });
    socket.on('connect', () => socket.emit('join', { channel: username, username: '' }));
    socket.on('raid', (data) => { setRaidBanner(data); setTimeout(() => setRaidBanner(null), 12000); });
    socket.on('poll', (data) => { setPoll(data); setVoted(false); });
    socket.on('poll_update', (data) => { setPoll(prev => prev && prev.id === data.poll_id ? { ...prev, votes: data.votes } : prev); });
    socket.on('poll_ended', (data) => { setPoll(prev => prev && prev.id === data.poll_id ? { ...prev, votes: data.votes, ended: true } : prev); });
    socket.on('goal_update', (data) => { setGoal({ text: data.goal_text, progress: data.goal_progress, target: data.goal_target }); });
    return () => socket.disconnect();
  }, [username]);

  // Theater mode keyboard shortcut
  const handleKeyDown = useCallback((e) => {
    if (e.key === 't' && !e.ctrlKey && !e.metaKey && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
      setTheater(prev => !prev);
    }
  }, []);
  useEffect(() => { document.addEventListener('keydown', handleKeyDown); return () => document.removeEventListener('keydown', handleKeyDown); }, [handleKeyDown]);

  const handleVote = async (index) => {
    if (!poll || voted) return;
    try {
      const res = await api.votePoll(poll.id, { option: index });
      setPoll(prev => ({ ...prev, votes: res.votes }));
      setVoted(true);
    } catch (e) { setVoted(true); }
  };

  const handleTip = async () => {
    if (!streamData) return;
    setTipping(true); setTipStatus('');
    try {
      await api.tip(username, { amount_cents: tipAmount, message: tipMsg, tipper_name: tipName || 'Anonymous' });
      setTipStatus(`Tip of $${(tipAmount / 100).toFixed(2)} sent!`);
    } catch (e) { setTipStatus(e.message); }
    finally { setTipping(false); }
  };

  const handleSubscribe = async () => {
    if (!user) { setSubStatus('Please log in'); return; }
    setSubscribing(true); setSubStatus('');
    try { await api.subscribe(username, { amount_cents: 499 }); setSubStatus('Subscribed for $4.99/month!'); }
    catch (e) { setSubStatus(e.message); }
    finally { setSubscribing(false); }
  };

  const handleFollow = async () => {
    if (!user) return;
    try {
      if (following) { await api.unfollow(username); setFollowing(false); }
      else { await api.follow(username); setFollowing(true); }
    } catch (e) {}
  };

  const handleGiftSub = async () => {
    setGifting(true); setGiftStatus('');
    try {
      const res = await api.giftSub(username, giftRandom ? { random: true } : { recipient: giftRecipient });
      setGiftStatus(`Gifted a sub to ${res.recipient_display}!`);
      setTimeout(() => { setShowGift(false); setGiftStatus(''); }, 3000);
    } catch (e) { setGiftStatus(e.message); }
    finally { setGifting(false); }
  };

  if (loading) return <div className="loading-screen">Loading...</div>;
  if (!streamData) return <div className="loading-screen">Channel not found</div>;

  const streamer = streamData.user || streamData;
  const isLive = streamData.is_live;
  const playbackId = streamer.mux_playback_id;
  const totalVotes = poll ? poll.votes.reduce((a, b) => a + b, 0) : 0;

  return (
    <div className={`watch-layout ${theater ? 'theater-mode' : ''}`}>
      {/* Raid banner */}
      {raidBanner && (
        <div style={{ gridColumn: '1 / -1', background: 'linear-gradient(135deg, #D6E9AA, #a8bf7e)', borderRadius: '2px', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', animation: 'raidPulse 0.5s ease', color: '#1a1a1a', boxShadow: '3px 3px 0 rgba(0,0,0,0.3)' }}>
          <div>
            <div style={{ fontSize: '1.3rem', fontWeight: 800 }}>{raidBanner.from_display} is raiding with {raidBanner.viewer_count} viewers!</div>
            <div style={{ fontSize: '0.9rem', opacity: 0.7, marginTop: '4px' }}>Welcome raiders! Say hello in chat</div>
          </div>
          <button className="btn" style={{ background: '#1a1a1a', color: '#D6E9AA', fontWeight: 700 }} onClick={() => navigate(`/${raidBanner.from_username}`)}>Visit {raidBanner.from_display}</button>
        </div>
      )}

      {/* Left: video + info */}
      <div className="video-section">
        <div className="video-player">
          {isLive && playbackId ? (
            <video src={`https://stream.mux.com/${playbackId}.m3u8`} controls autoPlay muted />
          ) : (
            <div className="offline-banner" style={{ aspectRatio: '16/9', borderRadius: 0 }}>
              <h2>{streamer.display_name || username} is offline</h2>
              <p>Follow to get notified when they go live</p>
            </div>
          )}
        </div>

        <div className="stream-info">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              {isLive && <h1>{streamData.title}</h1>}
              <div className="stream-meta">
                <span className="streamer-name">{streamer.display_name || username}</span>
                {isLive && <span className="live-badge" style={{ position: 'static' }}>LIVE</span>}
                {isLive && <span className="viewer-count">{streamData.viewer_count || 0} viewers</span>}
              </div>
            </div>
            {/* Theater toggle */}
            <button className="btn btn-secondary" onClick={() => setTheater(!theater)} title="Theater Mode (T)" style={{ padding: '6px 10px', fontSize: '0.75rem' }}>
              {theater ? 'Exit Theater' : 'Theater'}
            </button>
          </div>
          {isLive && streamData.tags?.length > 0 && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
              {streamData.tags.map(t => (
                <button key={t} onClick={() => navigate(`/?tag=${encodeURIComponent(t)}`)}
                  style={{ padding: '3px 10px', borderRadius: '2px', fontSize: '0.75rem', fontWeight: 600, background: '#2a2a2e', color: 'var(--accent)', border: 'none', cursor: 'pointer', boxShadow: '1px 1px 0 rgba(0,0,0,0.2)' }}>
                  {t}
                </button>
              ))}
            </div>
          )}
          <div className="stream-actions">
            {user && user.username !== username && (
              <button className={`btn ${following ? 'btn-secondary' : 'btn-outline'}`} onClick={handleFollow}>
                {following ? '✓ Following' : '+ Follow'}
              </button>
            )}
          </div>

          {/* Learning tags */}
          {isLive && streamData.learning_tags?.length > 0 && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '10px' }}>
              <span style={{ fontSize: '0.75rem', color: '#adadb8', fontWeight: 700 }}>Learn:</span>
              {streamData.learning_tags.map(t => (
                <span key={t} style={{ padding: '3px 10px', borderRadius: '2px', fontSize: '0.75rem', fontWeight: 600, background: '#D6E9AA22', color: 'var(--accent)', border: '1px solid #D6E9AA33', boxShadow: '1px 1px 0 rgba(0,0,0,0.1)' }}>
                  {t}
                </span>
              ))}
            </div>
          )}

          {/* Content rating */}
          {isLive && user && user.username !== username && (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.75rem', color: '#adadb8', fontWeight: 700 }}>Rate:</span>
              {[
                { key: 'helpful', label: 'Helpful', icon: '✦' },
                { key: 'entertaining', label: 'Fun', icon: '★' },
                { key: 'creative', label: 'Creative', icon: '◆' },
              ].map(r => (
                <button key={r.key}
                  className={`btn ${userRating === r.key ? 'btn-primary' : 'btn-outline'}`}
                  style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                  onClick={async () => {
                    const res = await api.rateStream(username, { rating: r.key });
                    setRatings(res.ratings);
                    setUserRating(r.key);
                  }}>
                  {r.icon} {r.label} {ratings[r.key] > 0 ? `(${ratings[r.key]})` : ''}
                </button>
              ))}
            </div>
          )}

          {/* Ratings display (non-interactive for guests) */}
          {isLive && (!user || user.username === username) && (ratings.helpful > 0 || ratings.entertaining > 0 || ratings.creative > 0) && (
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px', fontSize: '0.78rem', color: '#adadb8' }}>
              {ratings.helpful > 0 && <span>✦ {ratings.helpful} helpful</span>}
              {ratings.entertaining > 0 && <span>★ {ratings.entertaining} fun</span>}
              {ratings.creative > 0 && <span>◆ {ratings.creative} creative</span>}
            </div>
          )}
        </div>

        {/* Stream Goal */}
        {isLive && goal?.text && (
          <div className="panel-card" style={{ marginTop: '12px' }}>
            <div className="panel-title" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span>Stream Goal</span>
              <span style={{ fontSize: '0.78rem', color: 'var(--accent)', fontWeight: 800 }}>{goal.progress}/{goal.target}</span>
            </div>
            <div style={{ fontWeight: 700, marginBottom: '10px', fontSize: '0.9rem' }}>{goal.text}</div>
            <div style={{ background: '#0e0e10', borderRadius: '2px', height: '12px', overflow: 'hidden', boxShadow: 'inset 1px 1px 0 rgba(0,0,0,0.3)' }}>
              <div style={{
                width: `${Math.min(100, (goal.progress / goal.target) * 100)}%`,
                height: '100%',
                background: goal.progress >= goal.target ? '#00c400' : 'var(--accent)',
                transition: 'width 0.5s ease',
                borderRadius: '2px',
              }} />
            </div>
            {goal.progress >= goal.target && (
              <div style={{ marginTop: '8px', fontFamily: 'var(--font-pixel)', fontSize: '0.6rem', color: '#00c400', textAlign: 'center' }}>
                GOAL COMPLETE!
              </div>
            )}
          </div>
        )}

        {/* Active Poll */}
        {poll && !poll.ended && (
          <div className="panel-card" style={{ marginTop: '12px' }}>
            <div className="panel-title" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Poll</span>
              <span style={{ fontSize: '0.72rem', color: '#adadb8' }}>{totalVotes} votes</span>
            </div>
            <div style={{ fontWeight: 700, marginBottom: '10px' }}>{poll.question}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {poll.options.map((opt, i) => {
                const pct = totalVotes > 0 ? Math.round((poll.votes[i] / totalVotes) * 100) : 0;
                return (
                  <button key={i} onClick={() => handleVote(i)} disabled={voted}
                    style={{ position: 'relative', padding: '10px 12px', background: '#0e0e10', border: voted ? 'none' : '1px solid #2a2a2e', borderRadius: '2px', cursor: voted ? 'default' : 'pointer', textAlign: 'left', color: '#efeff1', fontSize: '0.85rem', fontWeight: 600, overflow: 'hidden', boxShadow: '1px 1px 0 rgba(0,0,0,0.2)' }}>
                    {voted && <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: 'var(--accent)', opacity: 0.15, transition: 'width 0.3s' }} />}
                    <span style={{ position: 'relative', zIndex: 1 }}>{opt}</span>
                    {voted && <span style={{ position: 'relative', zIndex: 1, float: 'right', color: '#adadb8' }}>{pct}%</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Right: chat + panels */}
      <div className="side-panel">
        <Chat channel={username} />

        {/* Channel Points */}
        {user && user.username !== username && (rewards.length > 0 || points !== null) && (
          <div className="panel-card">
            <div className="panel-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Channel Points</span>
              {points !== null && <span style={{ color: 'var(--accent)', fontSize: '1rem', fontWeight: 800 }}>{points.toLocaleString()} pts</span>}
            </div>
            {points === null && <p style={{ fontSize: '0.8rem', color: '#adadb8', marginBottom: '10px' }}>Watch live to earn 10 pts/min</p>}
            {rewards.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {rewards.map(r => (
                  <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: '#0e0e10', borderRadius: '4px' }}>
                    <div>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{r.title}</div>
                      {r.description && <div style={{ fontSize: '0.75rem', color: '#adadb8' }}>{r.description}</div>}
                    </div>
                    <button className="btn btn-primary" style={{ padding: '4px 10px', fontSize: '0.78rem', flexShrink: 0, marginLeft: '8px' }}
                      onClick={() => handleRedeem(r)} disabled={points === null || points < r.cost}>
                      {r.cost.toLocaleString()} pts
                    </button>
                  </div>
                ))}
              </div>
            )}
            {redeemStatus && <p style={{ fontSize: '0.82rem', marginTop: '8px' }}>{redeemStatus}</p>}
            <p style={{ fontSize: '0.72rem', color: '#adadb8', marginTop: '8px' }}>+10 pts/min while watching live</p>
          </div>
        )}

        {/* Subscribe + Gift */}
        <div className="panel-card">
          <div className="panel-title">Subscribe</div>
          <p style={{ fontSize: '0.85rem', color: '#adadb8', marginBottom: '12px' }}>
            Support {streamer.display_name || username} with $4.99/month.<br />
            <strong style={{ color: 'var(--accent)' }}>90%</strong> goes directly to the streamer.
          </p>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleSubscribe} disabled={subscribing}>
              {subscribing ? 'Processing...' : 'Subscribe $4.99/mo'}
            </button>
            {user && user.username !== username && (
              <button className="btn btn-outline" onClick={() => setShowGift(!showGift)} title="Gift a Sub">
                Gift
              </button>
            )}
          </div>
          {subStatus && <p style={{ fontSize: '0.85rem', marginTop: '8px' }}>{subStatus}</p>}

          {/* Gift sub form */}
          {showGift && (
            <div style={{ marginTop: '12px', padding: '12px', background: '#0e0e10', borderRadius: '4px' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: '8px' }}>Gift a Sub</div>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                <button className={`btn ${giftRandom ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1, fontSize: '0.78rem', padding: '6px' }}
                  onClick={() => setGiftRandom(true)}>Random Viewer</button>
                <button className={`btn ${!giftRandom ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1, fontSize: '0.78rem', padding: '6px' }}
                  onClick={() => setGiftRandom(false)}>Specific User</button>
              </div>
              {!giftRandom && (
                <input className="input" placeholder="Username" value={giftRecipient} onChange={e => setGiftRecipient(e.target.value)} style={{ marginBottom: '8px' }} />
              )}
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleGiftSub} disabled={gifting || (!giftRandom && !giftRecipient.trim())}>
                {gifting ? 'Gifting...' : 'Gift Sub $4.99'}
              </button>
              {giftStatus && <p style={{ fontSize: '0.82rem', marginTop: '6px', color: giftStatus.includes('Gifted') ? '#00c400' : '#eb0400' }}>{giftStatus}</p>}
            </div>
          )}
        </div>

        {/* Tip */}
        <div className="panel-card">
          <div className="panel-title">Send a Tip</div>
          <div className="tip-amounts">
            {TIP_PRESETS.map(p => (
              <button key={p} className={`tip-amount-btn ${tipAmount === p ? 'selected' : ''}`} onClick={() => setTipAmount(p)}>
                ${(p / 100).toFixed(0)}
              </button>
            ))}
          </div>
          <input className="input" type="number" min="1" placeholder="Custom amount ($)" value={tipAmount / 100} onChange={e => setTipAmount(Math.round(parseFloat(e.target.value || 0) * 100))} />
          <input className="input" placeholder="Your name (optional)" value={tipName} onChange={e => setTipName(e.target.value)} />
          <input className="input" placeholder="Message (optional)" value={tipMsg} onChange={e => setTipMsg(e.target.value)} />
          <p style={{ fontSize: '0.75rem', color: '#adadb8', marginBottom: '8px' }}>90% goes to streamer · 5% to charity</p>
          <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleTip} disabled={tipping || tipAmount < 100}>
            {tipping ? 'Sending...' : `Tip $${(tipAmount / 100).toFixed(2)}`}
          </button>
          {tipStatus && <p style={{ fontSize: '0.85rem', marginTop: '8px' }}>{tipStatus}</p>}
        </div>
      </div>
    </div>
  );
}
